import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Pause, Play, DollarSign, ChevronDown, ChevronRight, StickyNote, RefreshCw } from "lucide-react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";

const PAYOUT_BADGE = {
  paid: { text: "text-green-800", bg: "#D1FAE5" },
  void: { text: "text-gray-600", bg: "#E5E7EB" },
  pending: { text: "text-amber-800", bg: "#FEF3C7" },
};
import { useAuth } from "@/lib/auth";

/**
 * Admin — Affiliate management dashboard
 * -------------------------------------
 * Lists every affiliate (all statuses) with rolled-up stats and per-row
 * actions: approve / reject / pause / resume, mark payouts paid, view
 * per-affiliate conversions, edit internal notes.
 *
 * Route: /admin/affiliates (admin only)
 */
export default function AdminAffiliates() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ pending_owed_cents: 0, pending_owed_count: 0, paid_total_cents: 0, paid_total_count: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await api.get(`/admin/affiliates${q}`);
      setRows(data.affiliates || []);
      setTotals(data.totals || {});
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (user === null) return null;
  if (!user || user.role !== "admin") {
    return (
      <AppShell>
        <div className="mx-auto max-w-md px-6 py-14 text-center">
          <h1 className="font-display text-xl font-bold mb-2">Admin only</h1>
          <Link to="/" className="btn-primary inline-block mt-4">Back to home</Link>
        </div>
      </AppShell>
    );
  }

  const act = async (aid, verb, opts = {}) => {
    setBusyId(`${aid}:${verb}`);
    try {
      await api.post(`/admin/affiliates/${aid}/${verb}`, opts.body || {});
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const dollars = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-6 py-10" data-testid="admin-affiliates-page">
        <div className="flex items-start gap-3 mb-6">
          <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
            <DollarSign className="w-6 h-6" style={{ color: "var(--ai-text)" }} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold">Affiliates</h1>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              Approve external promoters, track their conversions, and mark payouts as paid.
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={load} className="btn-ghost text-sm inline-flex items-center gap-1"
                    data-testid="admin-affiliates-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <Link to="/admin/pricing" className="btn-ghost text-sm"
                  data-testid="admin-affiliates-pricing-link">
              Pricing config →
            </Link>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <TotalCard label="Owed to affiliates" value={dollars(totals.pending_owed_cents)}
                     sub={`${totals.pending_owed_count} pending`} tone="pending"
                     testId="admin-total-owed" />
          <TotalCard label="Paid out lifetime" value={dollars(totals.paid_total_cents)}
                     sub={`${totals.paid_total_count} paid`} tone="paid"
                     testId="admin-total-paid" />
          <TotalCard label="Active affiliates"
                     value={rows.filter((r) => r.status === "approved").length}
                     sub={`${rows.length} total applications`} tone="neutral"
                     testId="admin-total-active" />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-4">
          <label className="text-xs font-semibold uppercase tracking-wide"
                 style={{ color: "var(--text-secondary)" }}>Status</label>
          <select className="field-input" style={{ width: 200 }} value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="admin-affiliates-filter">
            <option value="all">All</option>
            <option value="pending">Pending review</option>
            <option value="approved">Approved</option>
            <option value="paused">Paused</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-md text-xs" style={{ background: "#FEF2F2", color: "#991B1B" }}
               data-testid="admin-affiliates-error">
            {err}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-center py-16"
               style={{ color: "var(--text-secondary)" }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div className="card-surface p-10 text-center text-sm"
               style={{ color: "var(--text-secondary)" }}>
            No affiliates matching this filter yet.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <AffiliateRow key={r.id} row={r} onAction={act} busyId={busyId}
                            reload={load} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function TotalCard({ label, value, sub, tone, testId }) {
  const color = tone === "pending"
    ? "var(--accent-copper)"
    : tone === "paid"
      ? "#065F46"
      : "var(--text-primary)";
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="text-xs uppercase tracking-wide mb-1"
           style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="font-display text-3xl font-extrabold" style={{ color }}>{value}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{sub}</div>
    </div>
  );
}

function AffiliateRow({ row, onAction, busyId, reload }) {
  const [open, setOpen] = useState(row.status === "pending");
  const [conv, setConv] = useState(null);
  const [notes, setNotes] = useState(row.notes || "");
  const [notesSaved, setNotesSaved] = useState(false);
  const isBusy = (verb) => busyId === `${row.id}:${verb}`;
  const stats = row.stats || {};
  const dollars = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

  const loadConv = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/affiliates/${row.id}`);
      setConv(data.conversions || []);
    } catch (e) { toast.error("Couldn't load conversions."); }
  }, [row.id]);
  useEffect(() => { if (open) loadConv(); }, [open, loadConv]);

  const saveNotes = async () => {
    try {
      await api.post(`/admin/affiliates/${row.id}/notes`, { notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 1500);
    } catch (e) {
      console.warn("[AdminAffiliates] notes save failed:", e?.message || e);
      toast.error("Couldn't save notes.");
    }
  };

  const markAllPaid = async () => {
    if (!window.confirm(`Mark ALL pending payouts (${dollars(stats.earnings_pending_cents)}) as paid to ${row.name}?`)) return;
    try {
      await api.post(`/admin/affiliates/${row.id}/mark-paid`, { paid_via: "manual" });
      await loadConv();
      await reload();
    } catch (e) { toast.error("Couldn't mark payouts as paid."); }
  };

  const voidConv = async (convId) => {
    if (!window.confirm("Void this conversion? (e.g. after a refund). This cannot be undone.")) return;
    try {
      await api.post(`/admin/affiliates/${row.id}/void-conversion/${convId}`);
      await loadConv();
      await reload();
    } catch (e) { toast.error("Couldn't void that conversion."); }
  };

  const StatusBadge = () => {
    const map = {
      pending: { bg: "#FEF3C7", fg: "#92400E", label: "Pending review" },
      approved: { bg: "#D1FAE5", fg: "#065F46", label: "Approved" },
      paused: { bg: "#E5E7EB", fg: "#374151", label: "Paused" },
      rejected: { bg: "#FEE2E2", fg: "#991B1B", label: "Rejected" },
    };
    const s = map[row.status] || map.pending;
    return (
      <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: s.bg, color: s.fg }}
            data-testid={`admin-affiliate-status-${row.id}`}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="card-surface" data-testid={`admin-affiliate-row-${row.id}`}>
      <div className="p-4 flex items-start gap-3">
        <button onClick={() => setOpen(!open)} className="mt-1"
                data-testid={`admin-affiliate-toggle-${row.id}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-display font-bold text-base">{row.name}</div>
            <StatusBadge />
            <code className="text-[11px] font-mono px-2 py-0.5 rounded"
                  style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}>
              {row.code}
            </code>
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {row.email} · {row.country || "—"} · Applied {row.applied_at?.slice(0, 10)}
          </div>
          <div className="mt-2 flex flex-wrap gap-3 text-xs" style={{ color: "var(--text-secondary)" }}>
            <span>{stats.clicks} clicks</span>
            <span>·</span>
            <span>{stats.signups} signups</span>
            <span>·</span>
            <span>{(stats.paid_conversions || 0) + (stats.pending_conversions || 0)} conversions</span>
            <span>·</span>
            <span style={{ color: "var(--accent-copper)", fontWeight: 700 }}>
              {dollars(stats.earnings_pending_cents)} owed
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {row.status === "pending" && (
            <>
              <button className="btn-primary text-xs inline-flex items-center gap-1"
                      onClick={() => onAction(row.id, "approve")}
                      disabled={isBusy("approve")}
                      data-testid={`admin-affiliate-approve-${row.id}`}>
                <Check className="w-3.5 h-3.5" /> Approve
              </button>
              <button className="btn-ghost text-xs inline-flex items-center gap-1"
                      onClick={() => onAction(row.id, "reject")}
                      disabled={isBusy("reject")}
                      data-testid={`admin-affiliate-reject-${row.id}`}>
                <X className="w-3.5 h-3.5" /> Reject
              </button>
            </>
          )}
          {row.status === "approved" && (
            <button className="btn-ghost text-xs inline-flex items-center gap-1"
                    onClick={() => onAction(row.id, "pause")}
                    disabled={isBusy("pause")}
                    data-testid={`admin-affiliate-pause-${row.id}`}>
              <Pause className="w-3.5 h-3.5" /> Pause
            </button>
          )}
          {row.status === "paused" && (
            <button className="btn-primary text-xs inline-flex items-center gap-1"
                    onClick={() => onAction(row.id, "resume")}
                    disabled={isBusy("resume")}
                    data-testid={`admin-affiliate-resume-${row.id}`}>
              <Play className="w-3.5 h-3.5" /> Resume
            </button>
          )}
          {stats.earnings_pending_cents > 0 && (
            <button className="btn-primary text-xs inline-flex items-center gap-1"
                    onClick={markAllPaid}
                    data-testid={`admin-affiliate-mark-paid-${row.id}`}>
              <DollarSign className="w-3.5 h-3.5" /> Mark {dollars(stats.earnings_pending_cents)} paid
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t p-4 space-y-4"
             style={{ borderColor: "var(--border-stone)" }}>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide mb-1"
                   style={{ color: "var(--text-secondary)" }}>How they'll promote</div>
              <div>{row.promotion_channel || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide mb-1"
                   style={{ color: "var(--text-secondary)" }}>Audience size · Payout</div>
              <div>{row.audience_size || "—"} · {row.payout_method || "—"} ({row.payout_email || "—"})</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-wide mb-1 inline-flex items-center gap-1"
                   style={{ color: "var(--text-secondary)" }}>
                <StickyNote className="w-3 h-3" /> Internal notes (only you see)
              </div>
              <textarea className="field-input font-mono text-xs" rows={2} value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid={`admin-affiliate-notes-${row.id}`} />
              <button className="btn-ghost text-xs mt-1" onClick={saveNotes}
                      data-testid={`admin-affiliate-notes-save-${row.id}`}>
                {notesSaved ? "Saved ✓" : "Save notes"}
              </button>
            </div>
          </div>

          {/* Conversions */}
          {conv === null ? (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Loading conversions…</div>
          ) : conv.length === 0 ? (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No conversions yet.</div>
          ) : (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide mb-2"
                   style={{ color: "var(--text-secondary)" }}>Conversions</div>
              <div className="space-y-1">
                {conv.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-xs p-2 rounded"
                       style={{ background: "var(--bg-cream)" }}
                       data-testid={`admin-affiliate-conv-${c.id}`}>
                    <span className="font-mono">{c.case_id?.slice(0, 8)} · {c.user_email || "—"}</span>
                    <span>{c.created_at?.slice(0, 10)}</span>
                    <span className="font-bold">{dollars(c.payout_cents)}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${(PAYOUT_BADGE[c.payout_status] || PAYOUT_BADGE.pending).text}`}
                       style={{ background: (PAYOUT_BADGE[c.payout_status] || PAYOUT_BADGE.pending).bg }}>
                      {c.payout_status}
                    </span>
                    {c.payout_status === "pending" && (
                      <button className="text-red-600 hover:underline"
                              onClick={() => voidConv(c.id)}
                              data-testid={`admin-affiliate-conv-void-${c.id}`}>Void</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
